// The gravitational lattice (key L): a grid of lines through all of space, anchored in the universe's own axes and bent toward the wells, like the picture of spacetime. Out in space only.
//
// It costs almost nothing. There is no texture, no light and no depth buffer: five static line sets (the same geometry, a 9 x 9 x 9 lattice of lines cut into short pieces, on the GPU
// once) and one small vertex shader, which does everything each frame from a few uniforms.
//
// LEVELS. A grid has one spacing, and space is the size of the sky, so there are five, each ten times the last (10^k0 m up to 10^(k0+4)), and k0 follows how far the observer is from the
// body that pulls it (log10 of that distance, less one). A level's weight is a bump over its spacing, centred on a third of that distance and reaching a hundredth and ten times it, and
// zero at the ends of the five, so a level appears and goes without a pop and about three are drawn at a time (the others are not drawn at all).
// Every level is a cube of lines about the observer, snapped to its own spacing in the universe's frame: flying through space moves past the lines, it does not carry them along.
//
// THE BEND. Each well pulls a point of the lattice a share w of the way to its centre, w = (s / (r + s))^2, r the point's distance from the well: all the way at its centre, a quarter at
// r = s, a hundredth at ten s. `s` is the well's radius times its escape speed over the home planet's, so a planet's funnel is its size and a star's reaches out to its pull. What every well
// does to the point under the observer (the displacement D0) is taken off, so the lattice near the eye is straight and the bend is the difference of the pulls, which is what the tides are.
// Inside a body's own radius the lines fade out; near a well they brighten with how much they were bent.
//
// Drawn straight into the picture (additive, no depth test, no tone mapping): it is a display, not a thing in the world, so exposure leaves it alone. The depth is not used and not clipped,
// so a lattice a hundred million kilometres across needs no far plane.
import * as THREE from 'three';
import type { V3 } from './bodies';

/** Lines a side of each level's lattice (odd: the observer's cell is in the middle), and the pieces each line is cut into. */
const LINES = 9;
const PIECES = 36;
/** Levels, and the most wells the shader bends toward. */
export const LEVELS = 5;
export const MAX_WELLS = 6;
/** The lattice's half-width in spacings. */
const HALF = (LINES - 1) / 2 + 0.5;

export interface Well {
  /** Centre in the star's frame (m), radius (m), and GM (any one unit for all: only the ratios matter). */
  pos: ArrayLike<number>;
  radius: number;
  gm: number;
}

const VERT = /* glsl */`
uniform float uSpacing;
uniform vec3 uOrigin;
uniform mat3 uRot;
uniform vec3 uD0;
uniform vec4 uWell[${MAX_WELLS}];
uniform float uWellR[${MAX_WELLS}];
uniform float uAlpha;
varying float vA;
varying float vB;
void main() {
  vec3 x = uOrigin + position * uSpacing;
  vec3 d = vec3(0.0);
  float bend = 0.0;
  float body = 1.0;
  for (int i = 0; i < ${MAX_WELLS}; i++) {
    float s = uWell[i].w;
    if (s <= 0.0) continue;
    vec3 dc = uWell[i].xyz - x;
    float r = length(dc);
    float k = s / (r + s);
    float w = k * k;
    d += dc * w;
    bend += w;
    body *= smoothstep(0.9 * uWellR[i], 1.1 * uWellR[i], r);
  }
  vec3 xp = x + d - uD0;
  float m = max(max(abs(position.x), abs(position.y)), abs(position.z)) / ${HALF.toFixed(1)};
  float edge = 1.0 - smoothstep(0.55, 1.0, m);
  float near = smoothstep(0.08 * uSpacing, 0.5 * uSpacing, length(xp));
  vA = uAlpha * edge * near * body;
  vB = min(bend, 1.0);
  vec4 c = projectionMatrix * vec4(uRot * xp, 1.0);
  c.z = 0.0;
  gl_Position = c;
}
`;

const FRAG = /* glsl */`
precision highp float;
varying float vA;
varying float vB;
void main() {
  if (vA < 0.003) discard;
  vec3 col = mix(vec3(0.20, 0.55, 0.95), vec3(1.0, 0.78, 0.35), vB);
  gl_FragColor = vec4(col, vA * (0.30 + 0.9 * vB));
}
`;

/** Lines along x, y and z through a LINES x LINES grid of offsets, each cut into PIECES pieces, as segments in units of the spacing. */
function lineGeometry(): THREE.BufferGeometry {
  const per = LINES * LINES * 3 * PIECES;
  const pos = new Float32Array(per * 6);
  let o = 0;
  const half = (LINES - 1) / 2;
  for (let axis = 0; axis < 3; axis++) {
    const a1 = (axis + 1) % 3, a2 = (axis + 2) % 3;
    for (let i = 0; i < LINES; i++) for (let j = 0; j < LINES; j++) {
      for (let k = 0; k < PIECES; k++) {
        for (let e = 0; e < 2; e++) {
          const t = ((k + e) / PIECES * 2 - 1) * HALF;
          pos[o + axis] = t; pos[o + a1] = i - half; pos[o + a2] = j - half;
          o += 3;
        }
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  return g;
}

export class Lattice {
  readonly group = new THREE.Group();
  private mats: THREE.ShaderMaterial[] = [];
  private rot = new THREE.Matrix3();
  private view = new THREE.Matrix3();
  private ax = new THREE.Matrix3();

  constructor() {
    const geo = lineGeometry();
    for (let k = 0; k < LEVELS; k++) {
      const m = new THREE.ShaderMaterial({
        vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false,
        uniforms: {
          uSpacing: { value: 1 }, uOrigin: { value: new THREE.Vector3() }, uRot: { value: new THREE.Matrix3() }, uD0: { value: new THREE.Vector3() },
          uWell: { value: Array.from({ length: MAX_WELLS }, () => new THREE.Vector4()) }, uWellR: { value: new Array<number>(MAX_WELLS).fill(1) }, uAlpha: { value: 0 },
        },
      });
      const mesh = new THREE.LineSegments(geo, m);
      mesh.frustumCulled = false;
      mesh.renderOrder = 900 + k;
      mesh.matrixAutoUpdate = false;
      this.group.add(mesh);
      this.mats.push(m);
    }
    this.group.visible = false;
    this.group.frustumCulled = false;
  }

  /**
   * Place and shade the lattice for this frame; `show` false hides it. `camera`: the view (its inverse world matrix turns render axes into the screen's); `axes`: the pose's universe-to-render
   * matrix (bodies.ts Pose.axes, rows); `obs`: the observer in the star's frame (m); `wells`: the bodies that pull; `home`: the one whose escape speed is the unit of the funnels' widths;
   * `scale`: how far the observer is from the body that pulls it (m).
   */
  update(show: boolean, camera: THREE.Camera, axes: ArrayLike<number>, obs: V3, wells: Well[], home: Well | null, scale: number): void {
    if (!show || !home || !(scale > 0)) { this.group.visible = false; return; }
    this.group.visible = true;
    // render axes (the pose's matrix: rows east, up, south) into the camera's view: view = R_cam^-1 * axes
    this.ax.set(axes[0], axes[1], axes[2], axes[3], axes[4], axes[5], axes[6], axes[7], axes[8]);
    this.view.setFromMatrix4(camera.matrixWorldInverse);
    this.rot.multiplyMatrices(this.view, this.ax);

    const e = Math.log10(scale), k0 = Math.floor(e) - 1;
    const vesc = (w: Well) => Math.sqrt(2 * w.gm / Math.max(w.radius, 1));
    const vh = vesc(home);
    const used = wells.slice(0, MAX_WELLS);
    // each well relative to the observer, its funnel width, and what all of them do to the point under the eye
    const rel: V3[] = used.map(w => [w.pos[0] - obs[0], w.pos[1] - obs[1], w.pos[2] - obs[2]]);
    const sw = used.map(w => w.radius * (vesc(w) / vh));
    let d0x = 0, d0y = 0, d0z = 0;
    for (let i = 0; i < used.length; i++) {
      const r = Math.hypot(rel[i][0], rel[i][1], rel[i][2]), k = sw[i] / (r + sw[i]), w = k * k;
      d0x += rel[i][0] * w; d0y += rel[i][1] * w; d0z += rel[i][2] * w;
    }
    for (let l = 0; l < LEVELS; l++) {
      const sp = Math.pow(10, k0 + l);
      const mat = this.mats[l], u = mat.uniforms;
      u.uSpacing.value = sp;
      // the lattice is anchored in the universe: its origin is the observer's place rounded to this level's spacing
      u.uOrigin.value.set(Math.round(obs[0] / sp) * sp - obs[0], Math.round(obs[1] / sp) * sp - obs[1], Math.round(obs[2] / sp) * sp - obs[2]);
      (u.uRot.value as THREE.Matrix3).copy(this.rot);
      u.uD0.value.set(d0x, d0y, d0z);
      const wv = u.uWell.value as THREE.Vector4[], wr = u.uWellR.value as number[];
      for (let i = 0; i < MAX_WELLS; i++) {
        if (i < used.length) { wv[i].set(rel[i][0], rel[i][1], rel[i][2], sw[i]); wr[i] = used[i].radius; } else { wv[i].set(0, 0, 0, 0); wr[i] = 1; }
      }
      const x = (k0 + l) - (e - 0.5), a = Math.max(0, 1 - Math.abs(x) / 1.5);
      u.uAlpha.value = Math.pow(a, 1.5);
      this.group.children[l].visible = a > 0.003;
    }
  }

  hide(): void { this.group.visible = false; }

  dispose(): void {
    const g = (this.group.children[0] as THREE.LineSegments | undefined)?.geometry;
    if (g) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}
